import { useRef, useState } from "react";
import { View, Text, Image, TouchableOpacity, Animated, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import type { Dog, DogStats } from "../app/lib/types";

interface DogTradingCardProps {
  dog: Dog;
  stats: DogStats | null;
  akcRank?: number | null;
  projectedRank?: number | null;
  totalInBreed?: number | null;
  /** Top speed in the breed for the current year — used for "X mph off the leader" fun fact. */
  topBreedSpeed?: number | null;
}

// Picks the most interesting, flattering fact about the dog from available data.
// Returns null if there's not enough data to make a meaningful claim.
function pickFunFact(
  dog: Dog,
  stats: DogStats | null,
  akcRank: number | null | undefined,
  projectedRank: number | null | undefined,
  totalInBreed: number | null | undefined,
  topBreedSpeed: number | null | undefined,
): string | null {
  if (!stats) return null;

  // 1. Climbing — projected better than official by a meaningful margin
  if (akcRank != null && projectedRank != null && projectedRank < akcRank && akcRank - projectedRank >= 3) {
    return `Watch out — climbing from #${akcRank} toward #${projectedRank}! 🚀`;
  }

  // 2. Close to next title
  if (stats.nextTitle && stats.pointsToNextTitle > 0 && stats.pointsToNextTitle < 100) {
    return `Sooo close to ${stats.nextTitle} — just ${stats.pointsToNextTitle.toFixed(2)} pts to go!`;
  }

  // 3. Closer pattern — run 2 noticeably faster than run 1
  const r1 = stats.run1AvgSpeed ?? 0;
  const r2 = stats.run2AvgSpeed ?? 0;
  if (r1 > 0 && r2 > 0 && r2 - r1 >= 0.5 && stats.run1Count >= 3 && stats.run2Count >= 3) {
    return `Don't sleep on Run 2 — I'm ${(r2 - r1).toFixed(2)} mph faster the second time around. 💨`;
  }

  // 4. Top X% of breed (officially ranked dogs only)
  if (akcRank != null && totalInBreed && totalInBreed >= 20) {
    const pct = Math.max(1, Math.round((akcRank / totalInBreed) * 100));
    if (pct <= 5) return `Top ${pct}% of ${dog.breed}s in the country. Elite club. 🏆`;
    if (pct <= 10) return `Top ${pct}% of ${dog.breed}s in the country. Not too shabby. 🥇`;
    if (pct <= 25) return `Top ${pct}% of ${dog.breed}s nationwide — and gaining ground. 🐾`;
  }

  // 5. Gap to the breed leader
  if (topBreedSpeed && topBreedSpeed > 0 && stats.bestThreeAvgSpeed > 0) {
    const gap = topBreedSpeed - stats.bestThreeAvgSpeed;
    if (gap > 0 && gap < 5) {
      return `Only ${gap.toFixed(2)} mph off the #1 ${dog.breed}. Coming for the crown. 👑`;
    }
  }

  // 6. Personal best brag
  if (stats.personalBestSpeed > 0 && stats.personalBestTime > 0) {
    return `Personal best: ${stats.personalBestSpeed.toFixed(2)} mph in just ${stats.personalBestTime.toFixed(2)}s. I. Am. SPEED. ⚡`;
  }

  // 7. Volume milestone — runs logged
  if (stats.totalRuns >= 100) {
    return `${stats.totalRuns} runs logged and still chasing the lure. 🐕💨`;
  }
  if (stats.totalRuns > 0) {
    return `${stats.totalRuns} runs logged. Just getting warmed up. 🔥`;
  }

  return null;
}

// Trading-card style profile that flips on tap to reveal stats.
// Sized 2.5:3.5 (real trading-card aspect) capped at 320x448.
const CARD_W = 320;
const CARD_H = 448;
const NAVY = "#1B2A4A";
const ORANGE = "#F5A623";

export function DogTradingCard({ dog, stats, akcRank, projectedRank, totalInBreed, topBreedSpeed }: DogTradingCardProps) {
  const flipped = useRef(new Animated.Value(0)).current;
  const [isFlipped, setIsFlipped] = useState(false);

  const flip = () => {
    Animated.timing(flipped, {
      toValue: isFlipped ? 0 : 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
    setIsFlipped(!isFlipped);
  };

  const frontRotate = flipped.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "180deg"] });
  const backRotate = flipped.interpolate({ inputRange: [0, 1], outputRange: ["180deg", "360deg"] });

  const displayRank = akcRank ?? projectedRank ?? null;
  const rankLabel = akcRank != null ? "AKC Rank" : projectedRank != null ? "Projected" : null;

  const currentTitle = stats?.currentTitle ?? null;
  const topSpeed = stats?.personalBestSpeed != null && stats.personalBestSpeed > 0 ? stats.personalBestSpeed.toFixed(2) : "—";
  const best3 = stats?.bestThreeAvgSpeed != null && stats.bestThreeAvgSpeed > 0 ? stats.bestThreeAvgSpeed.toFixed(2) : "—";
  const totalRuns = stats?.totalRuns ?? 0;
  const totalPoints = stats?.totalPoints != null ? stats.totalPoints.toFixed(2) : "—";
  const funFact = pickFunFact(dog, stats, akcRank, projectedRank, totalInBreed, topBreedSpeed);

  return (
    <View style={{ alignItems: "center", marginBottom: 16 }}>
      <TouchableOpacity activeOpacity={0.9} onPress={flip} style={{ width: CARD_W, height: CARD_H }}>
        {/* FRONT */}
        <Animated.View
          style={{
            position: "absolute",
            width: CARD_W,
            height: CARD_H,
            borderRadius: 18,
            overflow: "hidden",
            backgroundColor: NAVY,
            transform: [{ perspective: 1200 }, { rotateY: frontRotate }],
            backfaceVisibility: "hidden",
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.25,
            shadowRadius: 16,
            elevation: 8,
            borderWidth: 4,
            borderColor: ORANGE,
          }}
        >
          {dog.photo ? (
            Platform.OS === "web" ? (
              // Web: real <img> respects the owner-set vertical focal point (photoFocusY).
              // @ts-ignore — native HTML element on RN-web
              <img
                src={dog.photo}
                alt={dog.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: `center ${dog.photoFocusY ?? 50}%` } as any}
              />
            ) : (
              <Image source={{ uri: dog.photo }} style={{ width: "100%", height: "100%" }} resizeMode="cover" />
            )
          ) : (
            <View style={{ flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: NAVY }}>
              <Ionicons name="paw" size={64} color={ORANGE} />
            </View>
          )}

          {/* Top bar — Dash-Hound branding (compact) */}
          <View
            style={{
              position: "absolute",
              top: 0, left: 0, right: 0,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingHorizontal: 14,
              paddingVertical: 9,
              backgroundColor: "rgba(27,42,74,0.85)",
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <Ionicons name="paw" size={13} color={ORANGE} />
              <Text style={{ fontSize: 11, fontWeight: "800", color: ORANGE, letterSpacing: 1.5 }}>DASH-HOUND</Text>
            </View>
            {displayRank != null && (
              <View style={{ backgroundColor: ORANGE, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 }}>
                <Text style={{ fontSize: 11, fontWeight: "900", color: NAVY }}>#{displayRank}</Text>
              </View>
            )}
          </View>

          {/* Bottom panel — auto-sized to content so it's not 160px of wasted blue */}
          <View style={{ position: "absolute", bottom: 0, left: 0, right: 0, backgroundColor: "rgba(27,42,74,0.92)", paddingHorizontal: 16, paddingTop: 10, paddingBottom: 12 }}>
            <Text style={{ fontSize: 22, fontWeight: "900", color: "#ffffff", letterSpacing: -0.5, lineHeight: 26 }} numberOfLines={1}>
              {dog.name}
              {dog.registeredName ? (
                <Text style={{ fontSize: 13, fontWeight: "500", color: "#CBD5E1", fontStyle: "italic" }}>
                  {" - "}{dog.registeredName}
                </Text>
              ) : null}
            </Text>
            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginTop: 5 }}>
              <View style={{ backgroundColor: ORANGE, paddingHorizontal: 7, paddingVertical: 2, borderRadius: 5 }}>
                <Text style={{ fontSize: 10, fontWeight: "800", color: NAVY, letterSpacing: 0.3 }}>{dog.breed}</Text>
              </View>
              {dog.sex && (
                <Text style={{ fontSize: 11, color: "#CBD5E1", fontWeight: "600", textAlign: "right" }}>{dog.sex}</Text>
              )}
            </View>
          </View>

        </Animated.View>

        {/* BACK */}
        <Animated.View
          style={{
            position: "absolute",
            width: CARD_W,
            height: CARD_H,
            borderRadius: 18,
            overflow: "hidden",
            backgroundColor: NAVY,
            paddingHorizontal: 16,
            paddingTop: 14,
            paddingBottom: 12,
            transform: [{ perspective: 1200 }, { rotateY: backRotate }],
            backfaceVisibility: "hidden",
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.25,
            shadowRadius: 16,
            elevation: 8,
            borderWidth: 4,
            borderColor: ORANGE,
          }}
        >
          {/* Header */}
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <Ionicons name="paw" size={13} color={ORANGE} />
              <Text style={{ fontSize: 11, fontWeight: "800", color: ORANGE, letterSpacing: 1.5 }}>DASH-HOUND</Text>
            </View>
            {currentTitle && (
              <View style={{ backgroundColor: ORANGE, paddingHorizontal: 10, paddingVertical: 3, borderRadius: 6 }}>
                <Text style={{ fontSize: 12, fontWeight: "900", color: NAVY, letterSpacing: 0.5 }}>{currentTitle}</Text>
              </View>
            )}
          </View>

          {/* Name */}
          <Text style={{ fontSize: 22, fontWeight: "900", color: "#fff", letterSpacing: -0.5, lineHeight: 26 }} numberOfLines={1}>
            {dog.name}
          </Text>
          {dog.registeredName && (
            <Text style={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic", marginTop: 1 }} numberOfLines={1}>
              {dog.registeredName}
            </Text>
          )}

          {/* Rank callout */}
          {displayRank != null && (
            <View style={{ marginTop: 10, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: "rgba(245,166,35,0.12)", borderRadius: 10, borderWidth: 1, borderColor: "rgba(245,166,35,0.3)" }}>
              <Text style={{ fontSize: 10, color: ORANGE, fontWeight: "700", letterSpacing: 1.5, textTransform: "uppercase" }}>{rankLabel}</Text>
              <View style={{ flexDirection: "row", alignItems: "baseline", gap: 6, marginTop: 1, flexWrap: "wrap" }}>
                <Text style={{ fontSize: 30, fontWeight: "900", color: "#fff", letterSpacing: -1 }}>#{displayRank}</Text>
                {totalInBreed != null && (
                  <Text style={{ fontSize: 11, color: "#94A3B8", fontWeight: "600" }}>of {totalInBreed} {dog.breed}s</Text>
                )}
              </View>
            </View>
          )}

          {/* Stats grid */}
          <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            <StatBox label="Top Speed" value={`${topSpeed}`} unit="mph" />
            <StatBox label="Best 3 Avg" value={`${best3}`} unit="mph" />
            <StatBox label="Total Runs" value={`${totalRuns}`} unit="" />
            <StatBox label="Total Points" value={`${totalPoints}`} unit="pts" />
          </View>

          {/* Fun Fact — auto-picked from stats. Only renders when we can say something interesting. */}
          {funFact && (
            <View style={{ marginTop: 10, paddingVertical: 7, paddingHorizontal: 10, backgroundColor: "rgba(245,166,35,0.08)", borderRadius: 8, borderLeftWidth: 3, borderLeftColor: ORANGE }}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 5, marginBottom: 2 }}>
                <Ionicons name="sparkles" size={10} color={ORANGE} />
                <Text style={{ fontSize: 9, fontWeight: "800", color: ORANGE, letterSpacing: 1.2, textTransform: "uppercase" }}>Fun Fact</Text>
              </View>
              <Text style={{ fontSize: 11, color: "#fff", lineHeight: 15 }} numberOfLines={2}>{funFact}</Text>
            </View>
          )}

          {/* Footer — flows below content, no absolute positioning so it can't overlap. */}
          <View style={{ marginTop: "auto", paddingTop: 8, flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
            <Text style={{ fontSize: 10, color: "#64748B", fontWeight: "600", letterSpacing: 0.5 }}>dashhound.app</Text>
          </View>
        </Animated.View>
      </TouchableOpacity>

      {/* Flip hint — below card, centered */}
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 5, marginTop: 12, opacity: 0.7 }}>
        <Ionicons name="sync-outline" size={12} color="#94A3B8" />
        <Text style={{ fontSize: 11, color: "#94A3B8", fontWeight: "600", letterSpacing: 0.5 }}>
          {Platform.OS === "web" ? "Click card to flip" : "Tap card to flip"}
        </Text>
      </View>
    </View>
  );
}

function StatBox({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <View style={{ flex: 1, minWidth: "45%", backgroundColor: "rgba(255,255,255,0.06)", borderRadius: 10, paddingVertical: 9, paddingHorizontal: 11, borderWidth: 1, borderColor: "rgba(255,255,255,0.08)" }}>
      <Text style={{ fontSize: 9, color: "#94A3B8", fontWeight: "700", letterSpacing: 1, textTransform: "uppercase" }}>{label}</Text>
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 4, marginTop: 2 }}>
        <Text style={{ fontSize: 20, fontWeight: "900", color: "#fff", letterSpacing: -0.5 }}>{value}</Text>
        {unit ? <Text style={{ fontSize: 11, color: "#94A3B8", fontWeight: "600" }}>{unit}</Text> : null}
      </View>
    </View>
  );
}
